/**
 * Shared chrome for the Manual / Automated simulator feature columns:
 *
 *  - `FeatureCardShell`  — one gate/feature card with collapse + remove
 *    controls in the header (title, description, effect / speed badges and
 *    the optional enable switch supplied by the page).
 *  - `AddFeatureButton`  — "Add gate / feature" button + modal listing the
 *    features that are NOT currently on the page, each with its badges and
 *    an Add action.
 *  - `useCollapsedPanels` — collapsed-card titles persisted per page in
 *    localStorage so a tuned layout survives reloads.
 *
 * The pages decide what "active" means (toggle on / value > 0) and how
 * remove is applied to the config; this module only renders.
 */

import { Fragment, useCallback, useMemo, useState, type ReactNode } from "react";
import {
  ActionIcon,
  Anchor,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { IconChevronDown, IconChevronUp, IconChevronsDown, IconChevronsUp, IconPlus, IconSearch, IconX } from "@tabler/icons-react";

import {
  COST_LABEL,
  EFFECT_LABEL,
  FeatureMetaBadges,
  effectiveGrade,
  resolveFeatureMeta,
  useGateTimings,
  type FeatureCost,
  type FeatureEffect,
} from "../lib/featureMeta";
import type { SimulationGateTimings } from "../api/schemas";
import { featureCopyFor } from "../lib/featureCopy";

export interface FeatureSectionLike {
  title: string;
  description: string;
  enabledKey?: string;
  metaKey?: string;
}

/**
 * Value-driven features (no `*_enabled` toggle): the card counts as active
 * while the field is > 0, and "remove" resets it to 0.
 */
export const VALUE_FEATURE_FIELDS: Record<string, string> = {
  entry_confirm: "entry_confirm_seconds",
  sim_execution_delay: "sim_execution_delay_seconds",
  loss_streak_threshold: "loss_streak_continue_threshold_usdc",
};

/** Value a value-driven feature gets when switched on without a remembered value. */
export const VALUE_FEATURE_DEFAULTS: Record<string, string> = {
  entry_confirm_seconds: "2",
  sim_execution_delay_seconds: "2",
  loss_streak_continue_threshold_usdc: "1",
};

/** The numeric field behind a value-driven section, if any. */
export function valueFieldFor(section: FeatureSectionLike): string | null {
  return section.metaKey ? VALUE_FEATURE_FIELDS[section.metaKey] ?? null : null;
}

/** Whether a section is part of the simulation given the page's string inputs. */
export function isSectionActive(
  section: FeatureSectionLike,
  inputs: Record<string, string>,
  opts: { extraActiveKeys?: Set<string> } = {},
): boolean {
  if (section.enabledKey) return inputs[section.enabledKey] === "true";
  const key = section.metaKey;
  if (!key || key === "core") return true;
  const field = VALUE_FEATURE_FIELDS[key];
  if (!field) return true;
  if (opts.extraActiveKeys?.has(field)) return true;
  const n = Number(inputs[field] ?? 0);
  return Number.isFinite(n) && n > 0;
}

export function isSectionRemovable(section: FeatureSectionLike): boolean {
  if (section.enabledKey) return true;
  return Boolean(section.metaKey && VALUE_FEATURE_FIELDS[section.metaKey]);
}

/**
 * Display order for the feature column and the Add picker. Gates are a pure
 * conjunction, so order never changes a result; we group by what a feature
 * does to the trade set and sort fastest → slowest inside each group so the
 * expensive cards sink to the bottom. The core card always leads.
 */
const EFFECT_ORDER: FeatureEffect[] = ["removes", "adds", "both", "neutral"];
const GROUP_TITLE: Record<FeatureEffect, string> = {
  removes: "Entry gates — filter trades",
  adds: "Overrides — add trades",
  both: "Exits & timing — change outcomes",
  neutral: "Sizing & bookkeeping",
};

export interface OrderedSection<T extends FeatureSectionLike> {
  section: T;
  /** Set on the first card of each group. */
  groupHeader: string | null;
}

export function sortSections<T extends FeatureSectionLike>(
  sections: T[],
  timings: SimulationGateTimings | undefined,
): OrderedSection<T>[] {
  const rank = (s: T) => {
    const key = s.metaKey ?? s.enabledKey;
    if (key === "core") return { group: -1, grade: 0 };
    const meta = resolveFeatureMeta(key);
    const group = meta ? EFFECT_ORDER.indexOf(meta.effect) : EFFECT_ORDER.length;
    const grade = effectiveGrade(key, timings)?.grade ?? 3;
    return { group, grade };
  };
  const sorted = sections
    .map((s, i) => ({ s, i, r: rank(s) }))
    .sort((a, b) => a.r.group - b.r.group || a.r.grade - b.r.grade || a.s.title.localeCompare(b.s.title) || a.i - b.i);
  let lastGroup: number | null = null;
  return sorted.map(({ s, r }) => {
    const header = r.group >= 0 && r.group !== lastGroup
      ? (GROUP_TITLE[EFFECT_ORDER[r.group] as FeatureEffect] ?? "Other")
      : null;
    lastGroup = r.group;
    return { section: s, groupHeader: header };
  });
}

/** `sortSections` bound to the live measured grades. */
export function useSortedSections<T extends FeatureSectionLike>(sections: T[]): OrderedSection<T>[] {
  const timings = useGateTimings();
  return useMemo(() => sortSections(sections, timings.data), [sections, timings.data]);
}

export function FeatureGroupHeader({ label }: { label: string }) {
  return (
    <Divider
      label={<Text size="xs" fw={600} tt="uppercase" c="dimmed" style={{ letterSpacing: 0.4 }}>{label}</Text>}
      labelPosition="left"
      mt="xs"
    />
  );
}

export function useCollapsedPanels(storageKey: string) {
  const [collapsed, setCollapsed] = useState<string[]>(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      const parsed = raw ? (JSON.parse(raw) as unknown) : null;
      return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
    } catch {
      return [];
    }
  });
  const persist = useCallback((next: string[]) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // storage unavailable — keep in-memory state only
    }
  }, [storageKey]);
  const toggle = useCallback((title: string) => {
    setCollapsed((prev) => {
      const next = prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title];
      persist(next);
      return next;
    });
  }, [persist]);
  const setAll = useCallback((titles: string[], value: boolean) => {
    setCollapsed((prev) => {
      const next = value ? Array.from(new Set([...prev, ...titles])) : prev.filter((t) => !titles.includes(t));
      persist(next);
      return next;
    });
  }, [persist]);
  const expand = useCallback((title: string) => {
    setCollapsed((prev) => {
      if (!prev.includes(title)) return prev;
      const next = prev.filter((t) => t !== title);
      persist(next);
      return next;
    });
  }, [persist]);
  const collapse = useCallback((title: string) => {
    setCollapsed((prev) => {
      if (prev.includes(title)) return prev;
      const next = [...prev, title];
      persist(next);
      return next;
    });
  }, [persist]);
  return { collapsed, toggle, setAll, expand, collapse };
}

/** Short summary with the long explanation behind a "Read more" toggle. */
export function FeatureDescription({
  section,
  size = "sm",
  lineClamp,
}: {
  section: FeatureSectionLike;
  size?: "xs" | "sm";
  lineClamp?: number;
}) {
  const copy = featureCopyFor(section.title, section.description);
  const [open, setOpen] = useState(false);
  return (
    <div>
      <Text className="feature-description" size={size} c="dimmed" lineClamp={open ? undefined : lineClamp}>
        {copy.summary}
        {copy.more ? (
          <>
            {" "}
            <Anchor
              component="button"
              type="button"
              size={size}
              onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
              aria-expanded={open}
            >
              {open ? "Show less" : "Read more"}
            </Anchor>
          </>
        ) : null}
      </Text>
      <Collapse in={open}>
        <Text size={size} c="dimmed" mt={4}>{copy.more}</Text>
      </Collapse>
    </div>
  );
}

export function FeatureCardShell({
  section,
  collapsed,
  onToggleCollapsed,
  onRemove,
  enabledControl,
  children,
}: {
  section: FeatureSectionLike;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Omit for the core card (not removable). */
  onRemove?: () => void;
  /** The page's enable Switch (or null for value-driven features). */
  enabledControl?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card withBorder data-feature-card={section.title}>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div style={{ flex: 1, minWidth: 0 }}>
            <UnstyledButton
              onClick={onToggleCollapsed}
              aria-expanded={!collapsed}
              aria-label={`${collapsed ? "Expand" : "Collapse"} ${section.title}`}
              style={{ display: "block", width: "100%" }}
            >
              <Group gap={6} wrap="nowrap" align="flex-start">
                {collapsed
                  ? <IconChevronDown size={18} style={{ marginTop: 4, flexShrink: 0 }} />
                  : <IconChevronUp size={18} style={{ marginTop: 4, flexShrink: 0 }} />}
                <Text className="feature-title" size="lg" fw={700}>{section.title}</Text>
              </Group>
            </UnstyledButton>
            <div style={{ paddingLeft: 24 }}>
              {!collapsed ? <FeatureDescription section={section} /> : null}
              <FeatureMetaBadges metaKey={section.metaKey ?? section.enabledKey} />
            </div>
          </div>
          <Group gap={6} wrap="nowrap" align="center" mt={2}>
            {enabledControl}
            {onRemove ? (
              <Tooltip label="Remove from this simulation (turns the feature off)" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={`Remove ${section.title}`}
                  onClick={onRemove}
                >
                  <IconX size={16} />
                </ActionIcon>
              </Tooltip>
            ) : null}
          </Group>
        </Group>
        <Collapse in={!collapsed}>
          <Stack gap="md">{children}</Stack>
        </Collapse>
      </Stack>
    </Card>
  );
}

/** "Add feature" + collapse-all / expand-all toolbar for the feature column. */
export function FeatureColumnToolbar({
  available,
  onAdd,
  onCollapseAll,
  onExpandAll,
}: {
  available: FeatureSectionLike[];
  onAdd: (section: FeatureSectionLike) => void;
  onCollapseAll: () => void;
  onExpandAll: () => void;
}) {
  return (
    <Group gap="xs" wrap="nowrap" align="center">
      <div style={{ flex: 1, minWidth: 0 }}>
        <AddFeatureButton available={available} onAdd={onAdd} />
      </div>
      <Tooltip label="Collapse every card" withArrow>
        <ActionIcon variant="default" size="lg" aria-label="Collapse all feature cards" onClick={onCollapseAll}>
          <IconChevronsUp size={18} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Expand every card" withArrow>
        <ActionIcon variant="default" size="lg" aria-label="Expand all feature cards" onClick={onExpandAll}>
          <IconChevronsDown size={18} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

export function AddFeatureButton({
  available,
  onAdd,
  disabled,
}: {
  available: FeatureSectionLike[];
  onAdd: (section: FeatureSectionLike) => void;
  disabled?: boolean;
}) {
  const [opened, setOpened] = useState(false);
  const [query, setQuery] = useState("");
  const [effectFilter, setEffectFilter] = useState<string[]>([]);
  const [speedFilter, setSpeedFilter] = useState<string[]>([]);
  const timings = useGateTimings();
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matching = available.filter((s) => {
      if (q && !(s.title.toLowerCase().includes(q) || s.description.toLowerCase().includes(q))) return false;
      const key = s.metaKey ?? s.enabledKey;
      const meta = resolveFeatureMeta(key);
      if (effectFilter.length > 0 && !(meta && effectFilter.includes(meta.effect))) return false;
      if (speedFilter.length > 0) {
        const g = effectiveGrade(key, timings.data)?.grade;
        if (g == null || !speedFilter.includes(String(g))) return false;
      }
      return true;
    });
    return sortSections(matching, timings.data);
  }, [available, query, effectFilter, speedFilter, timings.data]);
  const hasFilters = query.trim() !== "" || effectFilter.length > 0 || speedFilter.length > 0;
  return (
    <>
      <Button
        variant="light"
        leftSection={<IconPlus size={16} />}
        onClick={() => setOpened(true)}
        disabled={disabled || available.length === 0}
        fullWidth
      >
        Add gate / feature{available.length ? ` (${available.length} available)` : ""}
      </Button>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title="Add a gate or feature to this simulation"
        size="lg"
      >
        <Stack gap="sm">
          <TextInput
            placeholder="Search by name or description"
            leftSection={<IconSearch size={16} />}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            autoFocus
          />
          <Group gap="xs" align="center" wrap="wrap">
            <Text size="xs" c="dimmed" w={48}>Effect</Text>
            <Chip.Group multiple value={effectFilter} onChange={setEffectFilter}>
              <Group gap={6} wrap="wrap">
                {(Object.keys(EFFECT_LABEL) as FeatureEffect[]).map((e) => (
                  <Chip key={e} value={e} size="xs" color={EFFECT_LABEL[e].color} variant="light">
                    {EFFECT_LABEL[e].label}
                  </Chip>
                ))}
              </Group>
            </Chip.Group>
          </Group>
          <Group gap="xs" align="center" wrap="wrap">
            <Text size="xs" c="dimmed" w={48}>Speed</Text>
            <Chip.Group multiple value={speedFilter} onChange={setSpeedFilter}>
              <Group gap={6} wrap="wrap">
                {([1, 2, 3, 4, 5] as FeatureCost[]).map((g) => (
                  <Chip key={g} value={String(g)} size="xs" variant="light" styles={{ label: { color: COST_LABEL[g].color } }}>
                    {COST_LABEL[g].short}
                  </Chip>
                ))}
              </Group>
            </Chip.Group>
            {hasFilters ? (
              <Button
                size="compact-xs"
                variant="subtle"
                color="gray"
                onClick={() => { setQuery(""); setEffectFilter([]); setSpeedFilter([]); }}
              >
                Clear filters
              </Button>
            ) : null}
          </Group>
          <Text size="xs" c="dimmed">
            Showing {filtered.length} of {available.length} available
          </Text>
          <ScrollArea.Autosize mah="60vh" type="auto">
            <Stack gap="xs">
              {filtered.length === 0 ? (
                <Text size="sm" c="dimmed">
                  {available.length === 0
                    ? "Every available feature is already on the page."
                    : "No features match the current filters."}
                </Text>
              ) : filtered.map(({ section: s, groupHeader }) => (
                <Fragment key={s.title}>
                  {groupHeader ? <FeatureGroupHeader label={groupHeader} /> : null}
                  <Card withBorder padding="sm">
                    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text fw={600}>{s.title}</Text>
                        <FeatureDescription section={s} lineClamp={3} />
                        <FeatureMetaBadges metaKey={s.metaKey ?? s.enabledKey} size="xs" />
                      </div>
                      <Button
                        size="xs"
                        leftSection={<IconPlus size={14} />}
                        onClick={() => { onAdd(s); setOpened(false); }}
                        style={{ flexShrink: 0 }}
                      >
                        Add
                      </Button>
                    </Group>
                  </Card>
                </Fragment>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      </Modal>
    </>
  );
}
